import { useState } from 'react';
import { line, billCreditStatusSentence, billTakesCredit, formatCurrency, formatDateOnly, Size, t, type AvailableCreditRow } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites';
import { Money } from '../../components/Money';
import { NumericInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { useDateFormat } from '../../shell/ViewerZone';
import { BILLS_PATH, post, useApi } from './api';
import styles from './ApplyCreditModal.module.css';

/** The path the modal reads and the one it posts to; both hang off the Bill's own routes. */
export const creditApplicationsPath = (billId: string): string => `${BILLS_PATH}/${billId}/credit-applications`;
export const applyCreditPath = (billId: string): string => `${BILLS_PATH}/${billId}/apply-credit`;
/** The vendor's available credit, the list the Bill's banner and the New Bill form draw, read where no Bill is open. */
export const availableCreditPath = (vendorCompanyId: string): string => `${BILLS_PATH}/available-credit/${vendorCompanyId}`;

export interface CreditOfferView {
  kind: 'VendorCredit' | 'Prepayment';
  id: string;
  reference: string;
  amountCents: number;
  dated: string;
  maximumApplicableCents: number;
}

export interface CreditApplicationView {
  id: string;
  kind: 'VendorCredit' | 'Prepayment';
  sourceId: string;
  reference: string;
  billId: string;
  amountCents: number;
  appliedOn: string;
  closedAt: string | null;
}

export interface ApplyCreditScreenView {
  billId: string;
  billNumber: string;
  /** The Bill's status as its page draws it; the window says why a bill not ready takes no credit (T740). */
  billStatus: string;
  vendorCompanyId: string;
  vendorName: string;
  openBalanceCents: number;
  credits: CreditOfferView[];
  applications: CreditApplicationView[];
}

/** What the window is opened on: a Bill, which may take a credit, or a vendor, whose credits are only read. */
export type CreditWindowSource = { billId: string } | { vendorCompanyId: string };

/**
 * Apply credit (piece 27, Vendor credit application; piece 24, Vendor credits).
 *
 * 🔒 The list is the one the Bill's banner draws, vendor credits and vendor
 * prepayments both (piece 24, Vendor credits), so the banner never names a
 * credit this window says is not there. Each row carries its number, its date,
 * what is left of it and a pick (T740), drawn through the detail list and never
 * a bare table (piece 4).
 *
 * 🔒 The window is also the read-only View credits (T740): opened on a vendor,
 * or on a Bill whose status takes no credit, it lists the same rows with no pick
 * and no Apply, and on a Bill it says in a sentence naming the status why.
 *
 * 🔨 A person applies a named credit to this Bill, for an amount they choose.
 * Partial is ordinary and the remainder stays available for the next Bill.
 * Nothing applies a credit on its own, so this modal is the only way in.
 */
export function ApplyCreditModal({ source, chosen: preChosen, apiBaseUrl = '', onCancel, onDone = () => undefined }: {
  source: CreditWindowSource;
  /** 🔒 A credit the window opens with already picked, at the most one act can apply: the Vendor Credit's own page names itself (decision 7580). */
  chosen?: { kind: 'VendorCredit' | 'Prepayment'; id: string };
  apiBaseUrl?: string;
  onCancel: () => void;
  onDone?: (message: string) => void;
}) {
  const billId = 'billId' in source ? source.billId : null;
  const screen = useApi<ApplyCreditScreenView>(billId ? creditApplicationsPath(billId) : null, apiBaseUrl);
  const vendorRows = useApi<{ rows: AvailableCreditRow[] }>('vendorCompanyId' in source ? availableCreditPath(source.vendorCompanyId) : null, apiBaseUrl);
  const format = useDateFormat();
  const answer = screen.state.kind === 'ready' ? screen.state.data : null;
  const read = billId ? screen.state : vendorRows.state;
  const rows: readonly (AvailableCreditRow & { maximumApplicableCents?: number })[] = billId ? (answer?.credits ?? []) : vendorRows.state.kind === 'ready' ? vendorRows.state.data.rows : [];
  // 🔒 Only a Bill whose status takes a credit is applied to; every other opening is View credits (T740).
  const applying = answer !== null && billTakesCredit(answer.billStatus);
  const notReady = answer ? billCreditStatusSentence(answer.billStatus) : null;
  const [creditId, setCreditId] = useState(preChosen?.id ?? '');
  /** What was typed; undefined until somebody types, when the amount is the most the chosen credit applies (the pick's own answer). */
  const [typedCents, setTypedCents] = useState<number | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /** 🔒 A refused apply reads inside the window, which stays open for the correction (decision 3063). */
  const [refusal, setRefusal] = useState<string | null>(null);
  const chosen = answer?.credits.find((row) => row.id === creditId) ?? null;
  const amountCents = typedCents !== undefined ? typedCents : chosen?.maximumApplicableCents ?? null;

  const apply = async () => {
    if (!chosen || !billId) return;
    setBusy(true);
    setRefusal(null);
    const result = await post<CreditApplicationView>(applyCreditPath(billId), { kind: chosen.kind, creditId: chosen.id, amountCents }, apiBaseUrl);
    setBusy(false);
    if (!result.ok) return setRefusal(result.message);
    onDone(line(t('bill.credit.applied'), formatCurrency({ amount: result.data.amountCents, currencyCode: 'USD' })));
  };

  const pick = (id: string) => {
    setCreditId(id);
    // The amount starts at the most this act can apply, which is the usual answer and always a legal one.
    setTypedCents(undefined);
  };

  const ready = chosen !== null && amountCents !== null && amountCents > 0 && amountCents <= chosen.maximumApplicableCents;
  return (
    <Modal
      open
      title={applying ? t('bill.credit.applyTitle') : t('bill.credit.viewTitle')}
      onCancel={onCancel}
      dataModal={applying ? 'applyCredit' : 'viewCredits'}
      width="wide"
      actions={applying ? [
        { kind: 'primary', label: t('bill.credit.applyAct'), onAct: () => void apply(), disabled: busy || !ready },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ] : [
        // 🔒 Decision 2140: View credits writes nothing, so it carries one Close (decision 3063).
        { kind: 'close', label: t('modal.close'), onAct: onCancel },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      {read.kind === 'failed' ? <p className={modalStyles.fieldLabel} data-apply-credit-error="true">{read.error}</p> : null}
      {/* 🔒 A bill not ready says why in the server's own sentence, naming its status, before anybody looks for an Apply (T740). */}
      {notReady ? <p className={styles.notReady} data-apply-credit-not-ready={answer?.billStatus}>{notReady}</p> : null}
      {answer ? <p className={modalStyles.fieldLabel} data-apply-credit-open={String(answer.openBalanceCents)}>{t('bill.credit.openBalance')}{' '}<Money cents={answer.openBalanceCents} /></p> : null}
      {!billId ? <p className={modalStyles.fieldLabel}>{t('bill.credit.vendorHint')}</p> : null}
      {/* 🔨 No credit left to apply is said plainly rather than offering an empty list with a dead button beneath it. */}
      {read.kind === 'ready' && rows.length === 0 ? <p className={modalStyles.fieldLabel} data-apply-credit-none="true">{t('bill.credit.none')}</p> : null}
      {/*
        🔒 The rows are the detail list's, the way Pay Bills lists bills with a pick (piece 4, the bare-table rule; T740):
        each names its credit, its date and what is left, and carries a round pick only when this Bill takes a credit.
      */}
      {rows.length > 0 ? (
        <div className={styles.credits} data-apply-credit-which="true" data-apply-credit-rows={rows.length}>
          <p className={styles.caption}>{t('bill.credit.which')}</p>
          <DetailList look="card" label={t('bill.credit.which')}>
            {rows.map((row) => {
              const label = line(row.kind === 'VendorCredit' ? t('bill.credit.vendorCredit') : t('bill.credit.prepayment'), row.reference);
              return (
                <DetailRow
                  key={row.id}
                  data-apply-credit-row={row.id}
                  data-apply-credit-kind={row.kind}
                  pick={applying ? <input type="radio" name="applyCreditPick" value={row.id} checked={creditId === row.id} onChange={() => pick(row.id)} aria-label={line(t('bill.credit.pick'), label)} data-apply-credit-pick={row.id} /> : undefined}
                  title={label}
                  detail={line(t('bill.credit.column.dated'), formatDateOnly(row.dated, format))}
                >
                  <span className={styles.amount} data-apply-credit-remaining={row.amountCents}>
                    <span className={styles.amountLabel}>{t('bill.credit.column.remaining')}</span>
                    <Money cents={row.amountCents} currencyCode="USD" />
                  </span>
                </DetailRow>
              );
            })}
          </DetailList>
        </div>
      ) : null}
      {applying && answer ? (
        <>
          <NumericInput size={Size.Medium} kind="currency" label={t('bill.credit.amount')} value={amountCents} onChange={setTypedCents} name="applyCreditAmount" />
          {chosen ? <p className={modalStyles.fieldLabel} data-apply-credit-max={String(chosen.maximumApplicableCents)}>{t('bill.credit.atMost')}{' '}<Money cents={chosen.maximumApplicableCents} /></p> : null}
          <p className={modalStyles.fieldLabel}>{t('bill.credit.partialHint')}</p>
        </>
      ) : null}
      {answer && answer.applications.length > 0 ? (
        <ul data-apply-credit-applied={String(answer.applications.length)}>
          {answer.applications.map((row) => (
            <li key={row.id} className={modalStyles.fieldLabel}>{row.reference}{' '}<Money cents={row.amountCents} />{' '}{row.appliedOn}</li>
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}
